import { useState } from "react";
import type { AnimeFilter } from "@/types/anime";
import { categories, regions, statuses, years } from "@/mocks/animeData";

interface FilterBarProps {
  filter: AnimeFilter;
  onChange: (patch: Partial<AnimeFilter>) => void;
  onReset: () => void;
  total: number;
  resultLabel?: string;
}

const sortOptions = [
  { value: "updated", label: "按更新时间" },
  { value: "popular", label: "按热度" },
  { value: "alpha", label: "按字母" },
];

interface SelectFieldProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}

function SelectField({ label, value, options, onChange }: SelectFieldProps) {
  return (
    <label className="flex flex-col gap-1.5 min-w-0">
      <span className="text-[11px] tracking-wider text-foreground-500">{label}</span>
      <span className="relative block">
        <select
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full h-10 pl-3 pr-8 rounded-md bg-background-50 border border-background-300/70 text-sm text-foreground-800 outline-none appearance-none cursor-pointer focus:border-accent-600 transition-colors"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value} className="bg-background-100">
              {option.label}
            </option>
          ))}
        </select>
        <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-500 pointer-events-none" />
      </span>
    </label>
  );
}

export default function FilterBar({ filter, onChange, onReset, total, resultLabel = "部作品" }: FilterBarProps) {
  const [panelOpen, setPanelOpen] = useState(false);

  const regionOptions = [{ value: "all", label: "全部地区" }, ...regions.map((item) => ({ value: item, label: item }))];
  const yearOptions = [{ value: "all", label: "全部年份" }, ...years.map((item) => ({ value: item, label: item }))];
  const statusOptions = [{ value: "all", label: "全部状态" }, ...statuses.map((item) => ({ value: item, label: item }))];

  return (
    <div className="w-full">
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
        <button
          type="button"
          onClick={() => onChange({ category: "all" })}
          className={`h-8 px-3.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
            filter.category === "all"
              ? "bg-primary-600 text-foreground-950"
              : "bg-background-200/70 text-foreground-600 hover:text-foreground-950"
          }`}
        >
          全部
        </button>
        {categories.map((item) => (
          <button
            key={item.slug}
            type="button"
            onClick={() => onChange({ category: item.slug })}
            className={`h-8 px-3.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
              filter.category === item.slug
                ? "bg-primary-600 text-foreground-950"
                : "bg-background-200/70 text-foreground-600 hover:text-foreground-950"
            }`}
          >
            {item.name}
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="hidden md:grid grid-cols-4 gap-3 flex-1 max-w-2xl">
          <SelectField
            label="地区"
            value={filter.region}
            options={regionOptions}
            onChange={(value) => onChange({ region: value })}
          />
          <SelectField
            label="年份"
            value={filter.year}
            options={yearOptions}
            onChange={(value) => onChange({ year: value })}
          />
          <SelectField
            label="更新状态"
            value={filter.status}
            options={statusOptions}
            onChange={(value) => onChange({ status: value })}
          />
          <SelectField
            label="排序方式"
            value={filter.sort}
            options={sortOptions}
            onChange={(value) => onChange({ sort: value as AnimeFilter["sort"] })}
          />
        </div>

        <div className="flex items-center justify-between gap-3 md:justify-end">
          <p className="text-xs text-foreground-500">
            共 <span className="text-accent-400 font-label tracking-wider text-sm">{total}</span> {resultLabel}
          </p>
          <button
            type="button"
            onClick={() => setPanelOpen(true)}
            className="md:hidden h-9 px-3 rounded-md border border-background-300/70 text-foreground-700 text-xs flex items-center gap-1.5 cursor-pointer"
          >
            <i className="ri-equalizer-line" />
            筛选
          </button>
          <button
            type="button"
            onClick={onReset}
            className="hidden md:inline-flex h-9 px-3 rounded-md border border-background-300/70 text-foreground-600 hover:text-accent-400 hover:border-accent-600/60 text-xs items-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        </div>
      </div>

      {panelOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setPanelOpen(false)}
            className="absolute inset-0 bg-background-50/80 backdrop-blur-sm"
          />
          <div className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-background-100 border-t border-background-300/70 p-4 pb-6 animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm text-foreground-950 tracking-wide">筛选与排序</h3>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                aria-label="关闭"
                className="w-8 h-8 rounded-md border border-background-300/70 text-foreground-500 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <SelectField label="地区" value={filter.region} options={regionOptions} onChange={(value) => onChange({ region: value })} />
              <SelectField label="年份" value={filter.year} options={yearOptions} onChange={(value) => onChange({ year: value })} />
              <SelectField label="更新状态" value={filter.status} options={statusOptions} onChange={(value) => onChange({ status: value })} />
              <SelectField label="排序方式" value={filter.sort} options={sortOptions} onChange={(value) => onChange({ sort: value as AnimeFilter["sort"] })} />
            </div>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  onReset();
                  setPanelOpen(false);
                }}
                className="flex-1 h-10 rounded-md border border-background-300/70 text-foreground-600 text-sm cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                className="flex-1 h-10 rounded-md bg-primary-600 hover:bg-primary-500 text-foreground-950 text-sm transition-colors cursor-pointer"
              >
                查看 {total} 部结果
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}